import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { FormikSendRecipientPicker, SendDialogActions } from "./SendRecipientPicker";
import { uniqueRecipients } from "./recipientOptions";

function buildRecipientOptions({ currentUser, suggestedRecipients, t }) {
  const options = [];
  const displayName = currentUser?.displayTitle || currentUser?.name;

  if (currentUser?.email) {
    options.push({
      key: "me",
      label: t("recipients.me"),
      name: displayName,
      email: currentUser.email,
      group: "team",
      channel: "work_email",
      preselect: true,
    });
  }
  if (currentUser?.private_email) {
    options.push({
      key: "me_private_email",
      label: t("recipients.channel.private_email"),
      name: displayName,
      email: currentUser.private_email,
      group: "team",
      channel: "private_email",
      preselect: false,
    });
  }

  (suggestedRecipients || []).forEach((recipient) => {
    if (!recipient?.email) return;
    const key = recipient.key || recipient.email;
    const isPrivate =
      recipient.channel === "private_email" ||
      String(key).endsWith("_private_email") ||
      String(key) === "me_private_email";
    options.push({
      key,
      label: recipient.label || recipient.name,
      name: recipient.name,
      email: recipient.email,
      group: recipient.group,
      channel: recipient.channel || (isPrivate ? "private_email" : "work_email"),
      // Context suggestions default on; private channels stay opt-in unless API says otherwise.
      preselect: !isPrivate && recipient.preselect !== false,
    });
  });

  return uniqueRecipients(options);
}

export default function DocumentationSendDialog({
  open,
  onClose,
  resourceType,
  resourceTitle,
  suggestedRecipients = [],
  showSignatureOption = false,
  defaultIncludeSignature = false,
  allowMultiple = false,
  onSend,
  onSent,
}) {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);

  const recipientOptions = useMemo(
    () => buildRecipientOptions({ currentUser, suggestedRecipients, t }),
    [currentUser, suggestedRecipients, t]
  );

  const preselected = useMemo(
    () => recipientOptions.filter((option) => option.preselect),
    [recipientOptions]
  );

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={
        resourceType === "journal"
          ? t("documentationSend.send_journal")
          : resourceType === "meeting_minutes"
            ? t("meeting_minutes.send")
            : t("documentationSend.send_document")
      }
      size="2xl"
      zIndex={3100}
      closeOnOutsideClick
    >
      <p className="mb-4 text-sm text-slate-600">
        {t("documentationSend.intro", { title: resourceTitle || "" })}
      </p>

      <Formik
        enableReinitialize
        initialValues={{
          name: allowMultiple ? "" : (preselected[0]?.name || ""),
          email: allowMultiple ? "" : (preselected[0]?.email || ""),
          message: "",
          signature: defaultIncludeSignature,
          // ME + suggested context recipients (e.g. employee subject) — not the full directory.
          recipients: allowMultiple ? preselected : [],
        }}
        validationSchema={Yup.object({
          email: allowMultiple ? Yup.string() : Yup.string().email(t("documentationSend.invalid_email")).required(t("documentationSend.email_required")),
          recipients: allowMultiple ? Yup.array().min(1, t("documentationSend.email_required")) : Yup.array(),
        })}
        onSubmit={async (values, helpers) => {
          try {
            let payload = values;
            if (allowMultiple) {
              const recipients = values.recipients.map(({ email, name }) => ({ email, name: name || email }));
              if (!recipients.length) {
                throw new Error(t("documentationSend.email_required"));
              }
              payload = { message: values.message, signature: values.signature, recipients };
            }

            const response = await onSend(payload);
            const sentCount = Array.isArray(response?.sent) ? response.sent.length : 0;
            const failed = Array.isArray(response?.failed) ? response.failed : [];
            const hasLog = Array.isArray(response?.send_log) && response.send_log.length > 0;
            const delivered = sentCount > 0 || hasLog || !!response?.last_sent_at;

            if ((response?.errors?.length || failed.length) && !delivered) {
              throw new Error(response.errors?.join(", ") || t("documentationSend.failed"));
            }

            // Mail may have been delivered even when send_log/history failed to persist.
            // Do not show a plain success that invites an immediate blind retry.
            if (response?.needs_follow_up) {
              const followUpMessage =
                (Array.isArray(response.errors) && response.errors.filter(Boolean).join(", ")) ||
                t("documentationSend.history_persist_failed");
              toast.error(followUpMessage);
            } else if (failed.length && delivered) {
              toast.error(
                t("meeting_minutes.send_partial", {
                  sent: sentCount || response.send_log.length,
                  failed: failed.length,
                })
              );
            } else {
              toast.success(
                resourceType === "journal"
                  ? t("documentationSend.success_journal")
                  : resourceType === "meeting_minutes"
                    ? t("meeting_minutes.send_success")
                    : t("documentationSend.success_document")
              );
            }
            onSent?.(response);
            onClose?.();
          } catch (error) {
            toast.error(error?.message || t("documentationSend.failed"));
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="space-y-5">
            <FormikSendRecipientPicker
              formik={formik}
              options={recipientOptions}
              multiple={allowMultiple}
              valueField={allowMultiple ? "recipients" : undefined}
              autoFocus
            />

            {showSignatureOption ? (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  name="signature"
                  checked={formik.values.signature}
                  onChange={formik.handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                {t("textDocuments.include_signature")}
              </label>
            ) : null}

            <PMField
              name="message"
              label={t("documentationSend.message")}
              multiline
              rows={5}
              placeholder={t("documentationSend.message_placeholder")}
              {...formik}
            />

            <SendDialogActions
              onCancel={onClose}
              onSubmit={formik.submitForm}
              submitting={formik.isSubmitting}
              cancelLabel={t("Cancel")}
              sendLabel={t("Send")}
              sendingLabel={t("Sending...")}
            />
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}
